/**
 * Bridge protocol — the shell ⇄ an embedded sibling application (yol, Kor, Bak, Yaz, …).
 *
 * This module owns the *shape* of the channel and the outbound side only. It is
 * deliberately free of React and free of any application domain import: the same
 * envelope has to serve every pane the shell hosts, today and after the suite is
 * split into separate repositories.
 *
 * Version 2 adds what a resource controller needs to act on a pane:
 *  - `host:suspend`   park the pane (stop rAF loops, drop caches) without dying
 *  - `host:resume`    undo a suspend
 *  - `host:metrics:request` / `metrics:report`  a pane's own cost of running
 *
 * Guarantees (unchanged from v1):
 *  - `postMessage` is addressed to a fixed origin (never `*`), and every inbound
 *    message is dropped unless `event.origin` matches and `src` is not us.
 *  - The sibling never sees a token: `hat` performs the API call itself.
 *  - Unknown message types are ignored, so a newer shell can talk to an older
 *    sibling and vice versa. Inbound `v` is accepted in `1..PROTOCOL_VERSION`.
 *
 * Extended for Kor-as-host:
 *  - Host identity is generalized: accepted host ids are `['hat', 'kor']`
 *  - New event layer: `suite:*` events for file lifecycle, theme, sync
 */

export const PROTOCOL_VERSION = 2;
/** Inbound protocol versions this shell still understands. */
export const ACCEPTED_VERSIONS: readonly number[] = [1, 2];

/** Which pane a channel belongs to — an application id from the registry. */
export type PaneId = string;

/** Accepted host identities (the shell is 'hat', Kor as host is 'kor'). */
export const ACCEPTED_HOST_IDS = ['hat', 'kor'] as const;
export type HostId = typeof ACCEPTED_HOST_IDS[number];

/** File operations a hosted pane may ask the shell to run on its behalf. */
export type PaneFsOp =
  | "read" | "readBinary" | "write" | "writeBinary" | "list" | "listDetailed"
  | "exists" | "mkdir" | "rm" | "stat" | "rename" | "dirname";

/**
 * Suite event types — fire-and-forget, no `req` field.
 * These ride the same postMessage channel as fs operations.
 */
export type SuiteEventType =
  | "suite:open-file"
  | "suite:file-opened"
  | "suite:file-saved"
  | "suite:dirty"
  | "suite:fs-changed"
  | "suite:open"
  | "suite:theme";

/** Payload for suite:open-file (Bak → Kor) */
export interface SuiteOpenFileEvent {
  type: "suite:open-file";
  appId: string;
  url: string;
  filePath: string;
  mode: 'edit' | 'reading';
}

/** Payload for suite:file-opened (app → host) */
export interface SuiteFileOpenedEvent {
  type: "suite:file-opened";
  appId: string;
  filePath: string;
}

/** Payload for suite:file-saved (app → host) */
export interface SuiteFileSavedEvent {
  type: "suite:file-saved";
  appId: string;
  filePath: string;
  via?: string;
}

/** Payload for suite:dirty (app → host) */
export interface SuiteDirtyEvent {
  type: "suite:dirty";
  filePath: string;
  dirty: boolean;
}

/** Payload for suite:fs-changed (host → all panes) */
export interface SuiteFsChangedEvent {
  type: "suite:fs-changed";
  folder: string;
  paths: string[];
}

/** Payload for suite:open (host → Bak) */
export interface SuiteOpenEvent {
  type: "suite:open";
  filePath: string;
  appId?: string;
}

/** Payload for suite:theme (host → all panes) */
export interface SuiteThemeEvent {
  type: "suite:theme";
  mode: string;
}

/** Union of all suite event payloads */
export type SuiteEvent =
  | SuiteOpenFileEvent
  | SuiteFileOpenedEvent
  | SuiteFileSavedEvent
  | SuiteDirtyEvent
  | SuiteFsChangedEvent
  | SuiteOpenEvent
  | SuiteThemeEvent;

/**
 * Is this inbound message addressable? One place for every guard a pane's
 * message loop needs, so no handler can forget an origin check.
 */
export function acceptable(
  data: unknown,
  eventOrigin: string,
  expectedOrigin: string
): (SuiteEvent & { v?: number; src?: string; type?: unknown }) | null {
  if (!data || typeof data !== "object") return null;
  if (!expectedOrigin || eventOrigin !== expectedOrigin) return null;
  const envelope = data as SuiteEvent & { v?: number; src?: string; type?: unknown };
  if (typeof envelope.type !== "string") return null;
  // Reject messages from hosts (hat/kor) - panes only receive from hosts
  if (envelope.src === 'hat' || envelope.src === 'kor') return null;
  const v = typeof envelope.v === "number" ? envelope.v : 1;
  return ACCEPTED_VERSIONS.includes(v) ? envelope : null;
}
